import { useEffect, useState } from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import type { StatusNotaFiscalTotem } from "@/api"
import type { ImpressaoCupomNota } from "@/lib/impressao-totem"
import { AvisoNotaFiscalTotem } from "@/components/totem/nota-fiscal-totem"
import StatusRing from "../../components/status-ring"

interface PagamentoRealizadoProps {
    valorFormatado: string
    formaRotulo: string
    nsu: string | null
    imprimiu: boolean
    /** Desfecho da NFC-e, quando houve emissão — mesmo bloco do totem de pedidos. */
    statusNota?: StatusNotaFiscalTotem | null
    /** O que foi feito do cupom: o cliente pode ter recusado, e a impressão pode ter falhado. */
    impressaoCupom?: ImpressaoCupomNota
    avisoEmissao: string | null
    onConcluir: () => void
}

const SEGUNDOS_RETORNO = 8

export default function PagamentoRealizado({ valorFormatado, formaRotulo, nsu, imprimiu, statusNota = null, impressaoCupom, avisoEmissao, onConcluir }: PagamentoRealizadoProps): JSX.Element {
    const [segundos, setSegundos] = useState(SEGUNDOS_RETORNO)

    useEffect(() => {
        const intervalo = setInterval(() => setSegundos((s) => Math.max(s - 1, 0)), 1000)
        const timer = setTimeout(onConcluir, SEGUNDOS_RETORNO * 1000)
        
        return () => {
            clearInterval(intervalo)
            clearTimeout(timer)
        }
    }, [onConcluir])

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
            }}
        >
            <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: 560 }}>
                <StatusRing status="sucesso" />

                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    Pagamento Realizado!
                </Typography>

                <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                    Obrigado pela preferência
                </Typography>

                <Paper variant="outlined" sx={{ width: '100%', maxWidth: 440, p: 3, mt: 2, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                        <LinhaResumo rotulo="Valor pago" valor={valorFormatado} />
                        <LinhaResumo rotulo="Forma" valor={formaRotulo} />
                        {nsu && <LinhaResumo rotulo="NSU" valor={nsu} />}
                    </Stack>
                </Paper>

                {statusNota && (
                    <Box sx={{ width: '100%', maxWidth: 440, mt: 1 }}>
                        <AvisoNotaFiscalTotem status={statusNota} impressao={impressaoCupom} comSenha={false} />
                    </Box>
                )}

                {avisoEmissao && (
                    <Typography variant="h6" sx={{ color: 'warning.main', mt: 1, fontWeight: 'bold' }}>
                        {avisoEmissao}
                    </Typography>
                )}

                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {segundos > 0 ? `Voltando ao início em ${segundos}s` : 'Voltando ao início...'}
                </Typography>
            </Stack>

            {imprimiu && !avisoEmissao && !statusNota && (
                <Box sx={{ position: 'absolute', bottom: 40, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                    <Paper elevation={0} sx={{ px: 4, py: 2, borderRadius: 3, backgroundColor: 'action.hover' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Cupom fiscal enviado para impressão!
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    )
}

function LinhaResumo({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
    return (
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary">
                {rotulo}
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {valor}
            </Typography>
        </Stack>
    )
}